import React from 'react';
import { Formik, Form, Field } from 'formik';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  useTheme,
  MenuItem as MuiMenuItem,
  Avatar,
  Button as MuiButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../../../modules/auth/context/AuthContext';
import { Button } from '../../../../shared/components/ui/Button';
import type { CompanySettings, MenuItem } from '../../types/settingsTypes';
import { ROLES } from '../../../../shared/constants/roles';

const SEGMENT_OPTIONS = [
  { value: 'Condomínio', label: 'Condomínio' },
  { value: 'Academia', label: 'Academia' },
  { value: 'Clínica', label: 'Clínica' },
  { value: 'Hospital', label: 'Hospital' },
  { value: 'Escola', label: 'Escola' },
  { value: 'Prédio Comercial', label: 'Prédio Comercial' },
  { value: 'Empresa', label: 'Empresa' },
  { value: 'Outros', label: 'Outros' },
];

const DEFAULT_MENU_ITEMS: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'DashboardRoundedIcon',
    to: '/dashboard',
    roles: [ROLES.ADMIN, ROLES.GESTOR, ROLES.USER],
    order: 0,
    isActive: true,
  },
  {
    id: 'tenants',
    label: 'Empresas',
    icon: 'ApartmentRoundedIcon',
    to: '/tenants',
    roles: [ROLES.ADMIN],
    order: 1,
    isActive: true,
  },
  {
    id: 'users',
    label: 'Usuários',
    icon: 'Groups2RoundedIcon',
    to: '/users',
    roles: [ROLES.ADMIN, ROLES.GESTOR],
    order: 2,
    isActive: true,
  },
  {
    id: 'residents',
    label: 'Moradores',
    icon: 'ContactEmergencyIcon',
    to: '/residents',
    roles: [ROLES.ADMIN, ROLES.GESTOR],
    order: 3,
    isActive: true,
  },
  {
    id: 'rooms',
    label: 'Áreas',
    icon: 'MeetingRoomIcon',
    to: '/rooms',
    roles: [ROLES.ADMIN, ROLES.GESTOR],
    order: 4,
    isActive: true,
  },
  {
    id: 'equipments',
    label: 'Equipamentos',
    icon: 'SettingsRoundedIcon',
    to: '/equipments',
    roles: [ROLES.ADMIN, ROLES.GESTOR],
    order: 5,
    isActive: true,
  },
  {
    id: 'lockers',
    label: 'Armários',
    icon: 'LockPersonIcon',
    to: '/lockers',
    roles: [ROLES.ADMIN, ROLES.GESTOR],
    order: 6,
    isActive: true,
  },
  {
    id: 'manufacturers',
    label: 'Fabricantes',
    icon: 'FactoryIcon',
    to: '/manufacturers',
    roles: [ROLES.ADMIN, ROLES.GESTOR],
    order: 7,
    isActive: true,
  },
  {
    id: 'visitors',
    label: 'Visitantes',
    icon: 'ContactPhoneIcon',
    to: '/visitors',
    roles: [ROLES.ADMIN, ROLES.GESTOR, ROLES.USER],
    order: 8,
    isActive: true,
  },
  {
    id: 'reservations',
    label: 'Reservas',
    icon: 'CalendarMonthIcon',
    to: '/reservations',
    roles: [ROLES.ADMIN, ROLES.GESTOR, ROLES.USER],
    order: 9,
    isActive: true,
  },
  {
    id: 'announcements',
    label: 'Anúncios',
    icon: 'AnnouncementIcon',
    to: '/announcements',
    roles: [ROLES.ADMIN, ROLES.GESTOR, ROLES.USER],
    order: 10,
    isActive: true,
  },
  {
    id: 'reports',
    label: 'Relatórios',
    icon: 'BookIcon',
    to: '/reports',
    roles: [ROLES.ADMIN, ROLES.GESTOR],
    order: 11,
    isActive: true,
  },
  {
    id: 'sectors',
    label: 'Setores',
    icon: 'MapsHomeWorkIcon',
    to: '/sectors',
    roles: [ROLES.ADMIN, ROLES.GESTOR],
    order: 12,
    isActive: true,
  },
  {
    id: 'units',
    label: 'Unidades',
    icon: 'AutoAwesomeMosaicIcon',
    to: '/units',
    roles: [ROLES.ADMIN, ROLES.GESTOR],
    order: 13,
    isActive: true,
  },
  {
    id: 'orders',
    label: 'Encomendas',
    icon: 'BookOnlineOutlined',
    to: '/orders',
    roles: [ROLES.ADMIN, ROLES.GESTOR, ROLES.USER],
    order: 14,
    isActive: true,
  },
];

interface SettingsFormProps {
  onCancel?: () => void;
  onSaved?: () => void;
}

// Componente SortableItem
function SortableItem({ item, index, theme, removeMenuItem, setFieldValue, values, showRoles }: {
  item: MenuItem;
  index: number;
  theme: any;
  removeMenuItem: (index: number, setFieldValue: any, currentValues: any) => void;
  setFieldValue: any;
  values: any;
  showRoles: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    ...(transform ? { transform: CSS.Transform.toString(transform) } : {}),
    transition,
    boxShadow: isDragging ? theme.shadows[4] : theme.shadows[1],
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: theme.palette.background.paper,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        mb: 1,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        transition: 'background 0.2s, box-shadow 0.2s',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.primary.main,
          },
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIndicatorIcon />
      </Box>

      <Box sx={{ flex: 1 }}>
        <TextField
          variant="standard"
          value={item.label}
          onChange={e => {
            const items = [...values.menuItems];
            items[index] = {
              ...items[index],
              label: e.target.value,
            };
            console.log('Label editado:', JSON.stringify(items[index], null, 2));
            setFieldValue('menuItems', items);
          }}
          fullWidth
          InputProps={{ disableUnderline: false }}
          sx={{ fontWeight: 500 }}
        />
        <Typography variant="caption" color="text.secondary">
          {item.to}
        </Typography>
      </Box>

      {showRoles && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {item.roles.map((role) => (
            <Chip
              key={role}
              label={role}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          removeMenuItem(index, setFieldValue, values);
        }}
        color="error"
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  );
}

export function SettingsForm({ onCancel, onSaved }: SettingsFormProps) {
  const theme = useTheme();
  const { settings, updateSettings, isUpdating, error, setError } = useSettings();
  const { user } = useAuth();
  const [showRoles, setShowRoles] = React.useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Pegar o nome da empresa do primeiro tenant do usuário
  const defaultCompanyName = user?.tenants?.[0]?.name || '';
  
  // Função para mapear o tipo do tenant para o valor do select
  const mapTenantTypeToSegment = (tenantType: string) => {
    const typeMap: { [key: string]: string } = {
      'Condomínio': 'Condomínio',
      'Academia': 'Academia',
      'Clínica': 'Clínica',
      'Hospital': 'Hospital',
      'Escola': 'Escola',
      'Prédio Comercial': 'Prédio Comercial',
      'Empresa': 'Empresa',
      'Outros': 'Outros',
    };
    return typeMap[tenantType] || 'Outros';
  };
  
  const defaultSegment = mapTenantTypeToSegment(user?.tenants?.[0]?.tenantType?.name || '');

  // Verificar se existe configuração no tenant type
  const hasConfig = settings?.config && Object.keys(settings.config).length > 0;
  
  console.log('Settings recebidos:', settings);
  console.log('Tem configuração?', hasConfig);
  console.log('Configuração:', settings?.config);
  
  // Se não tem config, usar valores padrão. Se tem, usar valores da resposta
  const getMenuItems = () => {
    if (hasConfig && settings?.config?.menuItems) {
      console.log('Usando menu items da configuração existente:', settings.config.menuItems);
      return settings.config.menuItems;
    } else {
      console.log('Usando menu items padrão:', DEFAULT_MENU_ITEMS);
      return DEFAULT_MENU_ITEMS;
    }
  };

  const getSegment = () => {
    if (hasConfig && settings?.config?.segment) {
      return settings.config.segment;
    }
    return defaultSegment;
  };

  const getCompanyName = () => {
    if (hasConfig && settings?.config?.companyName) {
      return settings.config.companyName;
    }
    return defaultCompanyName;
  };

  const getPrimaryColor = () => {
    if (hasConfig && settings?.config?.primaryColor) {
      return settings.config.primaryColor;
    }
    return '#3B4EFF';
  };

  const getSecondaryColor = () => {
    if (hasConfig && settings?.config?.secondaryColor) {
      return settings.config.secondaryColor;
    }
    return '#6B7280';
  };

  const initialValues: Partial<CompanySettings> = {
    segment: getSegment(),
    menuItems: getMenuItems(),
    companyName: getCompanyName(),
    primaryColor: getPrimaryColor(),
    secondaryColor: getSecondaryColor(),
  };

  const handleSubmit = async (values: Partial<CompanySettings>) => {
    try {
      const tenantTypeId = user?.tenants?.[0]?.typeId;
      
      if (!tenantTypeId) {
        console.error('TenantTypeId não encontrado');
        setError('ID do tenant não encontrado');
        return;
      }

      // Formato correto: config com menuItems
      const dataToSend = {
        config: {
          menuItems: values.menuItems,
          segment: values.segment,
          primaryColor: values.primaryColor,
          secondaryColor: values.secondaryColor,
          companyName: values.companyName,
        }
      };

      console.log('TenantTypeId:', tenantTypeId);
      console.log('Dados sendo enviados:', JSON.stringify(dataToSend, null, 2));
      console.log('Menu items sendo enviados:', JSON.stringify(values.menuItems, null, 2));

      await updateSettings({ 
        data: dataToSend, 
        tenantTypeId 
      });
      onSaved?.();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    }
  };

  const removeMenuItem = (index: number, setFieldValue: any, currentValues: any) => {
    const items = [...(currentValues.menuItems || [])];
    const removedItem = items[index];
    items.splice(index, 1);
    
    // Reordenar os itens restantes
    const reorderedItems = items.map((item, newIndex) => ({
      ...item,
      order: newIndex,
    }));
    
    console.log('Item removido:', JSON.stringify(removedItem, null, 2));
    console.log('Menu após remoção:', JSON.stringify(reorderedItems, null, 2));
    setFieldValue('menuItems', reorderedItems);
  };

  const handleDragEnd = (event: DragEndEvent, setFieldValue: any, currentValues: any) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const items = [...(currentValues.menuItems || [])];
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over?.id);

      const reorderedItems = arrayMove(items, oldIndex, newIndex);

      // Atualizar a ordem dos itens
      const updatedItems = reorderedItems.map((item, index) => ({
        ...item,
        order: index,
      }));

      console.log('Menu reordenado:', JSON.stringify(updatedItems, null, 2));
      setFieldValue('menuItems', updatedItems);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, handleChange }) => (
        <Form>
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={700} color={theme.palette.text.primary} mb={3}>
              Configurações da Empresa
            </Typography>

            <Grid container spacing={3}>
              {/* Informações Básicas */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} color={theme.palette.text.primary} mb={2}>
                  Informações Básicas
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  fullWidth
                  label="Nome da Empresa"
                  name="companyName"
                  disabled
                  value={values.companyName}
                  onChange={handleChange}
                  error={!!(touched.companyName && errors.companyName)}
                  helperText={touched.companyName && errors.companyName}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  select
                  fullWidth
                  disabled
                  label="Segmento"
                  name="segment"
                  value={values.segment}
                  onChange={handleChange}
                  error={!!(touched.segment && errors.segment)}
                  helperText={touched.segment && errors.segment}
                >
                  {SEGMENT_OPTIONS.map((option) => (
                    <MuiMenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MuiMenuItem>
                  ))}
                </Field>
              </Grid>

              {/* Configuração do Menu */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} color={theme.palette.text.primary} mb={2}>
                  Configuração do Menu
                </Typography>
                <Typography variant="body2" color={theme.palette.text.secondary} mb={2}>
                  Arraste e solte para reordenar os itens. Clique no chip para ativar/desativar itens do menu.
                </Typography>
                <MuiButton
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                  onClick={() => setShowRoles((prev) => !prev)}
                >
                  {showRoles ? 'Esconder Perfis' : 'Mostrar Perfis'}
                </MuiButton>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => handleDragEnd(event, setFieldValue, values)}
                    >
                      <SortableContext
                        items={values.menuItems?.map(item => item.id) || []}
                        strategy={verticalListSortingStrategy}
                      >
                        {values.menuItems?.map((item, index) => (
                          <SortableItem
                            key={item.id}
                            item={item}
                            index={index}
                            theme={theme}
                            removeMenuItem={removeMenuItem}
                            setFieldValue={setFieldValue}
                            values={values}
                            showRoles={showRoles}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Botões de Ação */}
            <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={isUpdating}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isUpdating}
              >
                {isUpdating ? 'Salvando...' : 'Salvar Configurações'}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
} 